// Checks how complete the numbers are before the AI Business Analyst answers from them.
// Pure functions only: no React, no network.

import { toWholeCents } from './money';

const DESC_MAX = 60;

// Whole cents for a valid amount (a finite, non-negative number or numeric string), else null.
function toCents(v) {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
  const n = typeof v === 'number' ? v : Number(v);
  // Negatives (refunds, credits) are excluded; zero amounts are counted.
  if (!Number.isFinite(n) || n < 0) return null;
  return toWholeCents(n);
}

// Line breaks and tabs become single spaces so one item can't break the prompt layout.
const oneLine = d => String(d == null ? '' : d).replace(/\s+/g, ' ').trim();

function trimDesc(d) {
  const s = oneLine(d);
  return s.length > DESC_MAX ? s.slice(0, DESC_MAX - 1).trimEnd() + '…' : s;
}

// laborCounts: does a job with no labor count as a gap for this business type (see src/businessPreset.js)? Default
// true. When false, jobsNoLabor stays 0, so the banner and prompt never mention missing labor.
// noCostsCounts: same idea for jobs with revenue and no costs (false for Solo services, where that is a soft note).
export function summarizeDataQuality({ jobSummaries, needsReview, laborCounts = true, noCostsCounts = true } = {}) {
  const items = [];
  (Array.isArray(needsReview) ? needsReview : []).forEach(it => {
    const cents = it ? toCents(it.amount) : null;
    if (cents === null) return;
    items.push({
      vendor: oneLine(it.vendor) || 'Unknown vendor',
      date: oneLine(it.date),
      cents,
      description: trimDesc(it.description),
    });
  });
  const totalCents = items.reduce((s, it) => s + it.cents, 0);
  // Largest first; ties keep their original order (sort is stable).
  const topUnassigned = items
    .map((it, i) => ({ it, i }))
    .sort((a, b) => b.it.cents - a.it.cents || a.i - b.i)
    .slice(0, 5)
    .map(({ it }) => ({ vendor: it.vendor, date: it.date, amount: it.cents / 100, description: it.description }));

  const jobs = (Array.isArray(jobSummaries) ? jobSummaries : []).filter(Boolean);
  const num = v => (typeof v === 'number' && Number.isFinite(v) ? v : Number(v) || 0);
  let jobsNoLabor = 0, jobsNoCosts = 0;
  jobs.forEach(j => {
    if (num(j.revenue) <= 0) return;
    if (laborCounts && !num(j.laborCost)) jobsNoLabor++;
    if (noCostsCounts && !num(j.costs)) jobsNoCosts++;
  });

  const unassignedCount = items.length;
  return {
    unassignedCount,
    unassignedTotal: totalCents / 100,
    topUnassigned,
    jobsNoLabor,
    jobsNoCosts,
    totalJobs: jobs.length,
    hasIssues: unassignedCount > 0 || jobsNoLabor > 0 || jobsNoCosts > 0,
  };
}

// Whole dollars when there are no cents ("$41,200"), otherwise exactly 2 decimals ("$1,234.50").
const money = n => {
  const v = Number(n) || 0;
  const hasCents = toWholeCents(v) % 100 !== 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: hasCents ? 2 : 0, maximumFractionDigits: 2,
  }).format(v);
};
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// Words for the selected date range: "from this year", "in the selected dates". "" for all dates.
export function periodPhrase(rangeKey) {
  switch (rangeKey) {
    case 'mtd': return 'from this month';
    case 'qtd': return 'from this quarter';
    case 'ytd': return 'from this year';
    case 'prior_year': return 'from last year';
    case 'custom': return 'in the selected dates';
    default: return '';
  }
}
const withPeriod = (text, period) => (period ? `${text} ${period}` : text);

// Compact plain-text section for the AI's system prompt. period is from periodPhrase().
export function dataQualityPromptSection(summary, period = '') {
  if (!summary || !summary.hasIssues) return 'DATA QUALITY: No known data issues.';
  const lines = [`DATA QUALITY (known issues${period ? `, ${period}` : ''}):`];
  if (period) lines.push('Expense and job counts below cover only the selected dates. The job list further down covers all dates.');
  if (summary.unassignedCount > 0) {
    lines.push(`- ${withPeriod(plural(summary.unassignedCount, 'expense', 'expenses'), period)} totaling ${money(summary.unassignedTotal)} not assigned to a job. Largest:`);
    summary.topUnassigned.slice(0, 5).forEach(t => {
      lines.push(`  ${t.vendor}, ${t.date || 'no date'}, ${money(t.amount)}`);
    });
  }
  if (summary.jobsNoLabor > 0) lines.push(`- ${withPeriod(plural(summary.jobsNoLabor, 'job', 'jobs'), period)} with revenue ${summary.jobsNoLabor === 1 ? 'has' : 'have'} no labor recorded.`);
  if (summary.jobsNoCosts > 0) lines.push(`- ${withPeriod(plural(summary.jobsNoCosts, 'job', 'jobs'), period)} with revenue ${summary.jobsNoCosts === 1 ? 'has' : 'have'} no costs recorded.`);
  return lines.join('\n');
}

// Words for the fixed notice above the chat.
export function dataQualityBannerText(summary, period = '') {
  if (!summary || !summary.hasIssues) return null;
  const title = "Your numbers aren't complete yet";
  const tail = 'Until these are sorted, answers may make margins look better than they really are.';
  const parts = [];
  if (summary.unassignedCount > 0) {
    parts.push(`${withPeriod(plural(summary.unassignedCount, 'expense', 'expenses'), period)} (${money(summary.unassignedTotal)}) ${summary.unassignedCount === 1 ? "hasn't" : "haven't"} been assigned to a job`);
  }
  if (summary.jobsNoLabor > 0) {
    parts.push(`${withPeriod(plural(summary.jobsNoLabor, 'job', 'jobs'), period)} ${summary.jobsNoLabor === 1 ? 'has' : 'have'} no labor recorded`);
  }
  let lead;
  if (parts.length > 0) {
    lead = parts.join(', and ');
  } else if (summary.jobsNoCosts > 0) {
    lead = `${withPeriod(plural(summary.jobsNoCosts, 'job', 'jobs'), period)} ${summary.jobsNoCosts === 1 ? 'has' : 'have'} no costs recorded`;
  } else return null;
  return { title, body: `${lead[0].toUpperCase()}${lead.slice(1)}. ${tail}` };
}

// Instructions added to the AI's intro text.
export function dataQualityInstructions(summary, dataSource, hasExpenseManagement = true) {
  const out = [];
  if (summary && summary.hasIssues) {
    const where = hasExpenseManagement ? ' in Expense Management first' : ' first';
    out.push("If DATA QUALITY lists issues, begin every answer that gives numbers or advice with a short line starting 'Data check:' (do not add it to a reply that only asks which dates the owner means) that states what is missing in plain numbers and names up to 3 of the largest unassigned expenses (vendor, date, amount). Say that margins and profit may look better than they really are until these are sorted, and suggest sorting them" + where + ". Then answer the question. Never describe margins as healthy or strong without that caveat when issues exist.");
  }
  return out.join(' ');
}

const SCREEN_RANGE_WORDS = {
  mtd: 'this month',
  qtd: 'this quarter',
  ytd: 'this year',
  prior_year: 'last year',
  custom: 'the custom dates the owner picked',
};

// Tells the AI which date range is selected on screen. It is a hint, not an answer:
// a period the owner names in chat always wins, and the AI only asks when none has been named.
const SCREEN_RANGE_ENDING = ' Use it only if the owner says "this period" or "what I\'m looking at", and you may mention it as one example if you need to ask which dates they mean. A period the owner names in chat always wins over the on-screen range. Only ask when no period of any kind has been named.';
export function periodScreenLine(rangeKey) {
  const words = SCREEN_RANGE_WORDS[rangeKey];
  if (!words) {
    return 'On screen, the owner is looking at all dates (all time).' + SCREEN_RANGE_ENDING;
  }
  return `On screen, the owner has selected ${words}.` + SCREEN_RANGE_ENDING;
}
